import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { format, formatDistanceToNow, parseISO } from "date-fns"

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/** Format ISO date string to human-readable */
export function formatDate(iso: string | null | undefined, fmt = "dd MMM yyyy, HH:mm"): string {
  if (!iso) return "—"
  try {
    return format(parseISO(iso), fmt)
  } catch {
    return "—"
  }
}

/** "2 hours ago" style relative time */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "—"
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true })
  } catch {
    return "—"
  }
}

/** Capitalise first letter of each word */
export function titleCase(str: string): string {
  return str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
  )
}

/** Extract initials from a full name */
export function getInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? "")
    .join("")
}

/** Truncate string with ellipsis */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str
  return str.slice(0, maxLength - 3) + "..."
}

/** Format a number with Indian numbering (1,00,000) */
export function formatIndianNumber(n: number): string {
  return new Intl.NumberFormat("en-IN").format(n)
}

/** Format KLD (Kilolitres per Day) */
export function formatKLD(value: number | null | undefined): string {
  if (value == null) return "—"
  return `${formatIndianNumber(value)} KLD`
}

/** Clamp a number between min and max */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/** Sleep for ms milliseconds (use sparingly) */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
